// @core/stores/affiliate/AffiliateCampaignStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  ActivateCampaignRequest,
  Campaign,
  CampaignResponse,
  CreateCampaignRequest,
  PauseCampaignRequest,
  UpdateCampaignRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateCampaignStore {
  // List state
  items: Campaign[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Campaign | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Campaign>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Campaign | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Campaign | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Activate campaign
   */
  async ActivateCampaign(campaign_id: string, data: ActivateCampaignRequest): Promise<CampaignResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ActivateCampaign(campaign_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create campaign for an offer
   */
  async CreateCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreateCampaign(data);
      const item = response.campaign;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get campaign by ID
   */
  async GetCampaign(campaign_id: string): Promise<Campaign> {
    // Check cache first
    const cached = this.cache.get(campaign_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetCampaign(campaign_id);
      const item = response.campaign;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List campaigns
   */
  async ListCampaigns(params?: PaginatedRequest & { affiliate_id?: string | number | boolean; offer_id?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListCampaigns(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause campaign
   */
  async PauseCampaign(campaign_id: string, data: PauseCampaignRequest): Promise<CampaignResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.PauseCampaign(campaign_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update campaign
   */
  async UpdateCampaign(campaign_id: string, data: UpdateCampaignRequest): Promise<Campaign> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.UpdateCampaign(campaign_id, data);
      const item = response.campaign;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const affiliateCampaignStore = new AffiliateCampaignStore();
